import type { Board, GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { asJxgPoint, type ElementCreatorContext } from '../core/board.types';
import { parseThemeColor, resolveColor } from '../core/color.utils';
import { deepMerge } from '../core/deepMerge';
import { bindLabel, createLineLabelContext, createPointLabelContext } from '../label/label.bind';
import { resolveLabelConfig } from '../label/label.model';
import { resolveAnchorAt } from '../label/label.position';
import { createPoint } from '../point/point.create';
import type { ElementTheme, LineStyle, PointStyle } from '../theme/theme.model';
import type { LineArgs, LineResult, PointInput } from './line.model';

/**
 * Check if input is an existing JSXGraph element.
 */
function isElement(input: PointInput): input is GeometryElement {
  return typeof input === 'object' && !Array.isArray(input) && 'elType' in input;
}

/**
 * Resolve vertex style with proper propagation order:
 * 1. theme.point
 * 2. theme.line.vertex
 * 3. style.vertex
 * 4. style.vertexPerIndex[index]
 */
function resolveVertexStyle(
  theme: ElementTheme,
  lineVertex?: PointStyle,
  styleVertex?: PointStyle,
  perIndex?: PointStyle,
): PointStyle {
  let result: PointStyle = { ...theme.point };
  result = deepMerge(result, theme.line.vertex);
  result = deepMerge(result, lineVertex);
  result = deepMerge(result, styleVertex);
  result = deepMerge(result, perIndex);
  return result;
}

/**
 * Build JSXGraph line attributes from style configuration.
 */
function buildLineAttributes(
  themeService: JsxgraphThemeService,
  baseStyle: ElementTheme['line'],
  styleOverrides?: LineStyle,
): Record<string, unknown> {
  const style = deepMerge(baseStyle, styleOverrides);
  const strokeColor = resolveColor(themeService, style.color);

  const attrs: Record<string, unknown> = {
    strokeColor,
    strokeWidth: style.strokeWidth,
    dash: style.dash,
    visible: style.visible,
    // Pass through JSXGraph's infinite line controls (default to false = segment)
    straightFirst: style.straightFirst ?? false,
    straightLast: style.straightLast ?? false,
    withLabel: false,
  };

  // Handle arrows
  if (styleOverrides?.arrows) {
    attrs['firstArrow'] = styleOverrides.arrows.first ?? false;
    attrs['lastArrow'] = styleOverrides.arrows.last ?? false;
  }

  return attrs;
}

/**
 * Compute the visible segment of a (possibly infinite) line clipped to the board bbox.
 * Returns [startX, startY, endX, endY] — the visible endpoints in the direction A→B.
 *
 * For a finite segment (both straight flags false), returns A→B as-is.
 * For infinite/extending lines, clips the line to the bounding box so that
 * anchorAt 0 = visible start and anchorAt 1 = visible end.
 */
function visibleLineSegment(
  A: { X: () => number; Y: () => number },
  B: { X: () => number; Y: () => number },
  board: Board,
  straightFirst: boolean,
  straightLast: boolean,
): [number, number, number, number] {
  const ax = A.X(),
    ay = A.Y(),
    bx = B.X(),
    by = B.Y();

  // Finite segment — return A→B directly
  if (!straightFirst && !straightLast) {
    return [ax, ay, bx, by];
  }

  const dx = bx - ax;
  const dy = by - ay;

  // Degenerate: both points coincide
  if (dx === 0 && dy === 0) {
    return [ax, ay, bx, by];
  }

  // bbox: [xMin, yMax, xMax, yMin]
  const bbox = board.getBoundingBox();
  const xMin = bbox[0],
    yMax = bbox[1],
    xMax = bbox[2],
    yMin = bbox[3];

  // Parametric: P(t) = A + t*(B-A).  t=0 → A, t=1 → B.
  // Clip to bbox using Liang-Barsky style parameter bounds.
  let tMin = straightFirst ? -1e6 : 0;
  let tMax = straightLast ? 1e6 : 1;

  // Clip against each bbox edge
  const clip = (p: number, q: number) => {
    if (p === 0) {
      return q >= 0;
    } // parallel to edge — inside if q >= 0
    const r = q / p;
    if (p < 0) {
      if (r > tMax) {
        return false;
      }
      if (r > tMin) {
        tMin = r;
      }
    } else {
      if (r < tMin) {
        return false;
      }
      if (r < tMax) {
        tMax = r;
      }
    }
    return true;
  };

  const inside = clip(-dx, ax - xMin) && clip(dx, xMax - ax) && clip(-dy, ay - yMin) && clip(dy, yMax - ay);

  if (!inside || tMin > tMax) {
    // Line is completely outside the bbox — fall back to A→B
    return [ax, ay, bx, by];
  }

  return [ax + tMin * dx, ay + tMin * dy, ax + tMax * dx, ay + tMax * dy];
}

/**
 * Create a line element with optional vertices and labels.
 * Labels are now configured via the style property:
 * - style.label: Label for the line itself
 * - style.vertex.label: Default label for both vertices
 * - style.vertexPerIndex[0].label / style.vertexPerIndex[1].label: Per-vertex label overrides
 *
 * Style propagation order for vertices:
 * 1. theme.point (base point defaults)
 * 2. theme.line.vertex (line-specific vertex defaults)
 * 3. style.vertex (user-provided vertex defaults)
 * 4. style.vertexPerIndex[0|1] (per-vertex overrides)
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Line creation arguments
 * @returns Line result with elements and optional labels
 */
export function createLine(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: LineArgs,
): LineResult {
  const { board } = ctx;
  const { a: inputA, b: inputB, name = '', style, vertexNames = ['A', 'B'] } = args;

  // Resolve or create vertices
  let pointA: GeometryElement;
  let pointB: GeometryElement;

  // Resolve vertex A
  if (isElement(inputA)) {
    pointA = inputA;
  } else if (typeof inputA === 'string') {
    // Look up point by name
    const found = board.elementsByName[inputA];
    if (!found) {
      throw new Error(`Point "${inputA}" not found on board`);
    }
    pointA = found as GeometryElement;
  } else {
    // Create new point from coords
    const vertexStyleA = resolveVertexStyle(theme, style?.vertex, style?.vertex, style?.vertexPerIndex?.[0]);
    const result = createPoint(ctx, themeService, theme, {
      coords: inputA,
      name: vertexNames[0],
      style: vertexStyleA,
    });
    pointA = result.point;
  }

  // Resolve vertex B
  if (isElement(inputB)) {
    pointB = inputB;
  } else if (typeof inputB === 'string') {
    const found = board.elementsByName[inputB];
    if (!found) {
      throw new Error(`Point "${inputB}" not found on board`);
    }
    pointB = found as GeometryElement;
  } else {
    const vertexStyleB = resolveVertexStyle(theme, style?.vertex, style?.vertex, style?.vertexPerIndex?.[1]);
    const result = createPoint(ctx, themeService, theme, {
      coords: inputB,
      name: vertexNames[1],
      style: vertexStyleB,
    });
    pointB = result.point;
  }

  // Build line attributes
  const lineAttrs = buildLineAttributes(themeService, theme.line, style);

  // Create the line (using 'line' element with straightFirst/straightLast controlling infinite extension)
  const line = board.create('line', [pointA, pointB], lineAttrs) as GeometryElement;

  const result: LineResult = { line, a: pointA, b: pointB };

  // Determine color for labels (strip shade — labels always use shade 500)
  const labelColor = parseThemeColor(style?.color ?? 'neutral').name;

  // Resolve vertex label configs from style hierarchy
  // Priority: style.vertexPerIndex[i].label > style.vertex.label
  const vertexALabelConfig = style?.vertexPerIndex?.[0]?.label ?? style?.vertex?.label;
  const vertexBLabelConfig = style?.vertexPerIndex?.[1]?.label ?? style?.vertex?.label;

  // Create label for vertex A if configured
  const labelConfigA = resolveLabelConfig(vertexALabelConfig, { color: labelColor });
  if (labelConfigA) {
    result.labelA = bindLabel({
      ctx,
      themeService,
      element: pointA,
      config: labelConfigA,
      contextProvider: createPointLabelContext(vertexNames[0], pointA),
    });
  }

  // Create label for vertex B if configured
  const labelConfigB = resolveLabelConfig(vertexBLabelConfig, { color: labelColor });
  if (labelConfigB) {
    result.labelB = bindLabel({
      ctx,
      themeService,
      element: pointB,
      config: labelConfigB,
      contextProvider: createPointLabelContext(vertexNames[1], pointB),
    });
  }

  // Create label for the line itself if configured - now from style.label
  const lineLabelConfig = resolveLabelConfig(style?.label, { color: labelColor });
  if (lineLabelConfig && name) {
    const typedA = asJxgPoint(pointA);
    const typedB = asJxgPoint(pointB);
    const t = resolveAnchorAt(lineLabelConfig.anchorAt);
    const isInfiniteFirst = style?.straightFirst ?? false;
    const isInfiniteLast = style?.straightLast ?? false;

    // For infinite/extending lines, compute the visible segment clipped to the bbox.
    // For finite segments fully within the bbox, this reduces to the A→B segment.
    const anchorPoint = board.create(
      'point',
      [
        () => {
          const [sx, , ex] = visibleLineSegment(typedA, typedB, board, isInfiniteFirst, isInfiniteLast);
          return sx + t * (ex - sx);
        },
        () => {
          const [, sy, , ey] = visibleLineSegment(typedA, typedB, board, isInfiniteFirst, isInfiniteLast);
          return sy + t * (ey - sy);
        },
      ],
      { visible: false },
    ) as GeometryElement;

    result.lineLabel = bindLabel({
      ctx,
      themeService,
      element: anchorPoint,
      config: lineLabelConfig,
      contextProvider: createLineLabelContext(name, line),
      slopeProvider: () => Math.atan2(typedB.Y() - typedA.Y(), typedB.X() - typedA.X()),
    });
  }

  return result;
}
